/*
  ================================================================
  INDIA PIN CODE VALIDATOR
  ================================================================

  Used by the Shopify Delivery Estimator.

  PURPOSE:
  - Check whether the entered PIN is a real Indian PIN code.
  - Return State / District information when available.
  - DO NOT calculate delivery dates here.

  The delivery estimator handles:
  - T+2 to T+3 dispatch
  - Mumbai / Maharashtra / India delivery buckets
  - Final estimated delivery date

  IMPORTANT:
  If a PIN cannot be verified, we DO NOT guess.
  ================================================================
*/


(function () {

  "use strict";


  /* ==============================================================
     DATA SOURCES
     ============================================================== */


  /*
    PRIMARY SOURCE

    India Post public PIN repository.

    Individual PINs are requested as:

    /400012.json
    /411001.json
    /700099.json
    etc.
  */

  var INDIA_POST_BASE =
    "https://raw.githubusercontent.com/IndiaPost/pin/master/api/v01/json/";


  /*
    FALLBACK SOURCE

    Used only if the primary lookup does not return usable data.
  */

  var FALLBACK_BASE =
    "https://api.postalpincode.in/pincode/";


  /* ==============================================================
     CACHE SETTINGS

     Once we've successfully verified a PIN, save the result
     in the customer's browser for 30 days.

     This makes repeat checks instant.
     ============================================================== */

  var CACHE_PREFIX =
    "delivery_pin_v1_";


  var CACHE_DAYS =
    30;



  /* ==============================================================
     BASIC PIN FORMAT CHECK
     ============================================================== */

  function isSixDigitPin(pin) {


    return /^[1-9][0-9]{5}$/.test(

      String(
        pin || ""
      ).trim()

    );

  }



  /* ==============================================================
     TEXT HELPERS
     ============================================================== */

  function cleanText(value) {


    return String(
      value || ""
    ).trim();

  }


  function cleanStatus(value) {


    return cleanText(
      value
    ).toLowerCase();

  }



  /* ==============================================================
     CACHE — READ
     ============================================================== */

  function getCached(pin) {


    try {


      var raw =
        localStorage.getItem(
          CACHE_PREFIX + pin
        );


      if (!raw) {

        return null;

      }


      var parsed =
        JSON.parse(raw);


      var maxAge =

        CACHE_DAYS *

        24 *

        60 *

        60 *

        1000;



      if (

        !parsed ||

        !parsed.savedAt ||

        Date.now() -
          parsed.savedAt >
          maxAge

      ) {


        localStorage.removeItem(
          CACHE_PREFIX + pin
        );


        return null;

      }


      return (
        parsed.value ||
        null
      );


    }

    catch (error) {


      return null;

    }

  }



  /* ==============================================================
     CACHE — SAVE
     ============================================================== */

  function setCached(
    pin,
    value
  ) {


    try {


      localStorage.setItem(

        CACHE_PREFIX + pin,

        JSON.stringify({

          savedAt:
            Date.now(),

          value:
            value

        })

      );


    }

    catch (error) {


      /*
        Cache failure should NEVER
        stop PIN validation.
      */

    }

  }



  /* ==============================================================
     TURN POST OFFICE DATA INTO ONE CLEAN RESULT
     ============================================================== */

  function makeResult(
    pin,
    records,
    source
  ) {


    if (

      !Array.isArray(records) ||

      records.length === 0

    ) {


      return null;

    }



    /*
      Find only records belonging
      to the exact requested PIN.
    */

    var matching =
      records.filter(
        function (item) {


          var itemPin =

            item.pincode ||

            item.Pincode ||

            item.PinCode ||

            item.PINCode ||

            "";


          return (
            String(itemPin) === pin
          );

        }
      );



    if (
      matching.length === 0
    ) {


      return null;

    }



    /* ============================================================
       DELIVERY STATUS

       A PIN can have multiple post offices.

       Prefer an office explicitly marked "Delivery".
       ============================================================ */

    var deliveryRecords =

      matching.filter(
        function (item) {


          var status =

            item.Deliverystatus ||

            item.DeliveryStatus ||

            item.Delivery ||

            "";


          return (

            cleanStatus(status) ===
            "delivery"

          );

        }
      );



    var preferred =

      deliveryRecords[0] ||

      matching[0];



    /* ============================================================
       RETURN CLEAN DATA
       ============================================================ */

    return {


      valid:
        true,


      /*
        True if at least one office for
        the PIN is marked Delivery.
      */

      deliverable:
        deliveryRecords.length > 0,


      pin:
        pin,


      state:

        cleanText(

          preferred.statename ||

          preferred.State ||

          preferred.state ||

          preferred.Circle ||

          ""

        ),


      district:

        cleanText(

          preferred.Districtname ||

          preferred.District ||

          preferred.district ||

          ""

        ),


      taluk:

        cleanText(

          preferred.Taluk ||

          preferred.Block ||

          preferred.taluk ||

          ""

        ),


      office:

        cleanText(

          preferred.officename ||

          preferred.Name ||

          preferred.OfficeName ||

          ""

        ),


      source:
        source,


      records:
        matching

    };

  }



  /* ==============================================================
     FETCH WITH TIMEOUT

     Prevents the delivery estimator from hanging indefinitely.
     ============================================================== */

  async function fetchWithTimeout(
    url,
    timeoutMilliseconds
  ) {


    var controller =
      new AbortController();


    var timer =
      setTimeout(

        function () {

          controller.abort();

        },

        timeoutMilliseconds

      );



    try {


      return await fetch(

        url,

        {

          method:
            "GET",

          mode:
            "cors",

          cache:
            "force-cache",

          signal:
            controller.signal,

          headers: {

            "Accept":
              "application/json"

          }

        }

      );


    }

    finally {


      clearTimeout(
        timer
      );

    }

  }



  /* ==============================================================
     PRIMARY LOOKUP — INDIA POST
     ============================================================== */

  async function lookupIndiaPost(pin) {


    try {


      var url =

        INDIA_POST_BASE +

        encodeURIComponent(pin) +

        ".json";



      var response =

        await fetchWithTimeout(

          url,

          4500

        );



      if (
        !response.ok
      ) {


        return null;

      }



      var data =
        await response.json();



      return makeResult(

        pin,

        data,

        "India Post"

      );


    }

    catch (error) {


      return null;

    }

  }



  /* ==============================================================
     FALLBACK LOOKUP
     ============================================================== */

  async function lookupFallback(pin) {


    try {


      var url =

        FALLBACK_BASE +

        encodeURIComponent(pin);



      var response =

        await fetchWithTimeout(

          url,

          4500

        );



      if (
        !response.ok
      ) {


        return null;

      }



      var payload =
        await response.json();



      if (

        !Array.isArray(payload) ||

        payload.length === 0 ||

        String(
          payload[0].Status || ""
        ).toLowerCase() !==
          "success" ||

        !Array.isArray(
          payload[0].PostOffice
        )

      ) {


        return null;

      }



      return makeResult(

        pin,

        payload[0].PostOffice,

        "Fallback PIN directory"

      );


    }

    catch (error) {


      return null;

    }

  }



  /* ==============================================================
     MAIN LOOKUP FUNCTION

     The delivery estimator will call:

     await window.IndiaPin.lookup("400012");

     ============================================================== */

  async function lookup(pin) {


    pin =

      String(
        pin || ""
      ).trim();



    /* ============================================================
       STEP 1 — FORMAT CHECK
       ============================================================ */

    if (
      !isSixDigitPin(pin)
    ) {


      return {

        valid:
          false,

        pin:
          pin,

        reason:
          "format"

      };

    }



    /* ============================================================
       STEP 2 — CHECK BROWSER CACHE
       ============================================================ */

    var cached =
      getCached(pin);



    if (
      cached
    ) {


      return cached;

    }



    /* ============================================================
       STEP 3 — INDIA POST
       ============================================================ */

    var indiaPostResult =

      await lookupIndiaPost(
        pin
      );



    if (
      indiaPostResult
    ) {


      setCached(

        pin,

        indiaPostResult

      );


      return indiaPostResult;

    }



    /* ============================================================
       STEP 4 — FALLBACK
       ============================================================ */

    var fallbackResult =

      await lookupFallback(
        pin
      );



    if (
      fallbackResult
    ) {


      setCached(

        pin,

        fallbackResult

      );


      return fallbackResult;

    }



    /* ============================================================
       STEP 5 — FAIL CLOSED

       CRITICAL:

       We NEVER say:
       "Couldn't find it, so let's assume Standard India."

       If we cannot verify the PIN,
       NO DELIVERY ESTIMATE should be shown.
       ============================================================ */

    return {

      valid:
        false,

      pin:
        pin,

      reason:
        "not_found"

    };

  }



  /* ==============================================================
     MAKE VALIDATOR AVAILABLE TO SHOPIFY
     ============================================================== */

  window.IndiaPin = {


    lookup:
      lookup,


    isSixDigitPin:
      isSixDigitPin

  };


})();